import AntIcon from "@/components/icons/AntIcon";
import { selectTagsStandardProps } from "@/utils/componentUtils";
import { skipInitialFormValues } from "@/utils/formUtils";
import { URLQuery } from "@/utils/queryUtils";
import { validationConstants, validations } from "@/utils/validationUtils";
import { Button, Flex, Form, Input, Radio, Select, Tooltip } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { rowGutter } from "../../../../../../common/constants";
import { AgentEducationFilterPageRequest } from "../../../../../agent/types";
import InstitutionSelect from "../../../../../enumerations/components/form/InstitutionSelect";
import { DEFAULT_POSTPONED_COMMISSIONS_QUERY_PARAMS } from "../../../../postponed/api";
import { PostponedCommissionsReport } from "../../../../postponed/enums";
import { PostponedCommissionsFilterPageRequest } from "../../../../postponed/types";
import { CommissionsBatchStep, PostponementReason, postponementReasonTMap } from "../../../enums";
import { CommissionsBatchBase } from "../../../types";
import PostponementReasonTag from "../../PostponementReasonTag";

interface Props {
  searchParams: PostponedCommissionsFilterPageRequest;
  batch?: CommissionsBatchBase;
}

export const BatchPostponedCommissionFilterForm = ({ searchParams, batch }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [form] = Form.useForm<PostponedCommissionsFilterPageRequest>();

  useEffect(() => {
    form.setFieldsValue({
      ...searchParams,
      report: searchParams.report ?? PostponedCommissionsReport.UNRESOLVED_INCLUDED_IN_BATCH,
    });
  }, [searchParams]);

  const handleFilterSubmit = (filter: AgentEducationFilterPageRequest) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            ...filter,
            ...skipInitialFormValues(filter, {
              ...DEFAULT_POSTPONED_COMMISSIONS_QUERY_PARAMS,
              report: PostponedCommissionsReport.UNRESOLVED_INCLUDED_IN_BATCH,
            }),
            pageIndex: undefined,
          },
          append: true,
        }),
      },
      { replace: true },
    );
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleFilterSubmit} name="postponedCommissionFilterForm">
      <Flex justify="center">
        <Form.Item name="report" noStyle>
          <Radio.Group
            size="large"
            buttonStyle="solid"
            optionType="button"
            onChange={(e) => {
              form.setFieldsValue({ report: e.target.value });
              handleFilterSubmit(form.getFieldsValue());
            }}
            options={[
              {
                label: (
                  <Tooltip
                    title={t("commissions.batch.helpers.postponedCommissionsIncludedInBatchTooltip")}
                    key={PostponedCommissionsReport.UNRESOLVED_INCLUDED_IN_BATCH}
                  >
                    {t("commissions.postponed.enums.report.UNRESOLVED_INCLUDED_IN_BATCH")}
                  </Tooltip>
                ),
                value: PostponedCommissionsReport.UNRESOLVED_INCLUDED_IN_BATCH,
              },
              batch?.step !== CommissionsBatchStep.FINISH
                ? {
                    label: (
                      <Tooltip
                        title={t("commissions.batch.helpers.postponedCommissionsTooltip")}
                        key={PostponedCommissionsReport.UNRESOLVED}
                      >
                        {t("commissions.postponed.enums.report.UNRESOLVED")}
                      </Tooltip>
                    ),
                    value: PostponedCommissionsReport.UNRESOLVED,
                  }
                : undefined,
            ].filter((item) => item !== undefined)}
          />
        </Form.Item>
      </Flex>

      <Flex justify="center" align="end" gap={rowGutter} className="margin-top-medium">
        <Form.Item
          style={{ width: 250 }}
          name="keyword"
          label={t("common.searchKeyword")}
          rules={[
            validations.size(
              validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
              validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
            ),
          ]}
        >
          <Input allowClear placeholder={t("commissions.batch.helpers.commissionsSearchHint")} />
        </Form.Item>

        <Form.Item
          style={{ width: 250 }}
          name="postponementReasons"
          label={t("commissions.batch.enums.postponementReason._label")}
          rules={[validations.none]}
        >
          <Select
            {...selectTagsStandardProps(postponementReasonTMap)}
            allowClear
            mode="multiple"
            maxTagCount="responsive"
            tagRender={(props) => (
              <PostponementReasonTag
                reason={props.value}
                closable={props.closable}
                onClose={props.onClose}
                label={props.label}
              />
            )}
            options={Object.values(PostponementReason).map((reason) => ({
              value: reason,
              label: <PostponementReasonTag reason={reason} />,
            }))}
          />
        </Form.Item>

        <InstitutionSelect
          formItemProps={{
            style: { width: 250 },
            name: "institutionIds",
            label: t("common.institution"),
            rules: [validations.none],
          }}
          selectProps={{ mode: "multiple", maxTagCount: "responsive", allowClear: true }}
          optionsProps={{ groupByType: true }}
        />

        <Form.Item>
          <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
            {t("common.filter")}
          </Button>
        </Form.Item>
      </Flex>
    </Form>
  );
};
